import React, { useEffect } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

/**
 * Invite de mise à jour et indicateur hors-ligne - lot 5.
 *
 * C'est la pièce qui manquait à la première tentative de PWA. Le service
 * worker bascule alors sans rien dire, et la coquille servie pouvait dater de
 * plusieurs versions. Comme elle embarque le moteur de calcul, une coquille
 * périmée annonce un prix faux sans que rien ne le signale.
 *
 * Ici la nouvelle version s'installe mais ATTEND. Rien ne change tant que
 * l'utilisateur n'a pas rechargé, et il sait exactement ce qu'il fait - ce qui
 * compte quand une cotation est peut-être en cours à l'écran.
 */
export default function MiseAJour() {
  const {
    needRefresh: [aRecharger, setARecharger],
    offlineReady: [pretHorsLigne, setPretHorsLigne],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(url, registration) {
      // Le navigateur ne cherche une nouvelle version qu'aux navigations. Sur
      // une application ouverte en permanence sur un téléphone, cela peut
      // vouloir dire jamais. Une vérification horaire est peu coûteuse - le
      // fichier fait quelques kilo-octets - et évite de rester bloqué sur une
      // version dont on ignore qu'elle est dépassée.
      if (!registration) return;
      setInterval(() => registration.update(), 60 * 60 * 1000);
    },
  });

  /**
   * Le bandeau est fixé au bas de la fenêtre, donc hors du flux : il masquait
   * ce qui se trouvait dessous - le titre « Sauvegarde » dans Réglages, le
   * dernier champ dans Coter. Réserver la hauteur pendant qu'il est affiché
   * coûte moins qu'un contenu inaccessible, et la réserve disparaît avec lui.
   */
  const visible = aRecharger || pretHorsLigne;
  useEffect(() => {
    const racine = document.documentElement;
    if (visible) racine.style.setProperty('--reserve-invite', '104px');
    else racine.style.removeProperty('--reserve-invite');
    return () => racine.style.removeProperty('--reserve-invite');
  }, [visible]);

  if (!visible) return null;

  const fermer = () => { setARecharger(false); setPretHorsLigne(false); };

  /**
   * Le rechargement est déclenché ici, et non laissé au greffon.
   *
   * `updateServiceWorker(true)` recharge via l'événement `controlling` de
   * workbox-window, dont le drapeau `isUpdate` est figé à l'inscription :
   * il vaut faux quand aucun service worker ne contrôlait encore la page,
   * c'est-à-dire lors de la toute première installation. Vérifié en réel -
   * le nouveau worker prenait bien le contrôle, mais la page ne se
   * rechargeait pas et l'utilisateur restait devant l'ancienne version avec
   * une invite qui venait de disparaître. Le pire des deux mondes.
   *
   * On écoute donc directement la prise de contrôle, avec un repli minuté au
   * cas où le nouveau worker contrôlerait déjà la page : un bouton
   * « Recharger » doit recharger, toujours.
   */
  const recharger = async () => {
    let fait = false;
    const relancer = () => { if (!fait) { fait = true; window.location.reload(); } };
    navigator.serviceWorker?.addEventListener('controllerchange', relancer, { once: true });
    setTimeout(relancer, 3000);
    await updateServiceWorker(false);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed', left: 'var(--espace-3)', right: 'var(--espace-3)',
        bottom: 'var(--espace-3)', zIndex: 60,
        maxWidth: 520, margin: '0 auto',
        display: 'flex', alignItems: 'center', gap: 'var(--espace-3)',
        flexWrap: 'wrap',
        padding: 'var(--espace-3) var(--espace-4)',
        background: 'var(--couleur-surface)',
        border: '1px solid var(--couleur-trait-fort)',
        borderRadius: 'var(--rayon)',
        boxShadow: '0 2px 12px rgb(27 35 51 / 14%)',
        fontSize: '0.85rem',
      }}
    >
      <span style={{ flex: '1 1 200px', minWidth: 0 }}>
        {aRecharger
          ? 'Une nouvelle version est prête. Elle s’appliquera au rechargement.'
          : 'Application disponible hors-ligne.'}
      </span>

      {/* Les deux boutons forment un groupe qui passe à la ligne ENSEMBLE.
          Laissés frères du texte dans le même flex, ils se séparaient sur un
          téléphone : « Recharger » restait collé au message et « Plus tard »
          tombait seul à la ligne suivante, aligné à gauche. */}
      <span style={{ display: 'flex', gap: 'var(--espace-2)', flexWrap: 'nowrap', marginLeft: 'auto' }}>
      {aRecharger && (
        <button
          type="button"
          onClick={recharger}
          style={{
            padding: 'var(--espace-2) var(--espace-4)',
            border: '1px solid var(--couleur-accent)',
            borderRadius: 'var(--rayon)',
            background: 'var(--couleur-accent)',
            color: 'var(--couleur-sur-accent)',
            fontWeight: 600,
          }}
        >
          Recharger
        </button>
      )}

      <button
        type="button"
        onClick={fermer}
        style={{
          padding: 'var(--espace-2) var(--espace-3)',
          border: '1px solid var(--couleur-trait-fort)',
          borderRadius: 'var(--rayon)',
          background: 'transparent',
          color: 'var(--couleur-encre)',
        }}
      >
        {/* Fermer ne renonce pas à la mise à jour : elle reste installée et
            s'appliquera à la prochaine ouverture. Écrire « Plus tard » plutôt
            que « Ignorer » dit ce qui se passe réellement. */}
        {aRecharger ? 'Plus tard' : 'Fermer'}
      </button>
      </span>
    </div>
  );
}
